import { useEffect, useRef, useState } from 'react';

/**
 * Anima um número de 0 (ou de um valor anterior) até o target.
 * Usa requestAnimationFrame com easing expo-out (signature do checkout).
 * Portado 1:1 do Reservei_Web (`src/lib/utils/useAnimatedNumber.ts`).
 *
 * @param target valor final
 * @param duration duração em ms (default 1200)
 * @param respectReducedMotion se true, pula animação quando user tem prefers-reduced-motion
 */
export function useAnimatedNumber(
  target: number,
  duration = 1200,
  respectReducedMotion = true,
): number {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Respeita prefers-reduced-motion
    if (
      respectReducedMotion &&
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      setValue(target);
      return;
    }

    const from = fromRef.current;
    const to = target;
    const start = performance.now();

    const tick = (now: number): void => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Cubic-bezier expo-out approximation: 1 - (1 - t)^4
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = from + (to - from) * eased;
      setValue(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, respectReducedMotion]);

  return value;
}
